/**
 * Stage 4a: Research Enrichment — context-based version using StagePanel.
 *
 * For each section's research needs, searches for citable sources via Tavily
 * and attaches results. Non-LLM stage.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const sections = document?.article?.sections || [];
  const sectionsWithResearch = sections.filter(
    (s) => s.research_needs && s.research_needs.length > 0
  );

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-gray-500 mb-1">
          Sections with research needs — {sectionsWithResearch.length} of {sections.length} sections
        </div>
        {sectionsWithResearch.length > 0 ? (
          <div className="space-y-1">
            {sectionsWithResearch.map((s) => (
              <div key={s.id} className="text-xs text-gray-600 border rounded px-2 py-1">
                <span className="font-medium">{s.heading || '(Intro)'}</span>
                <span className="text-gray-400 ml-2">
                  {s.research_needs.length} research {s.research_needs.length === 1 ? 'need' : 'needs'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-400">No research needs defined in the brief.</div>
        )}
      </div>
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const sectionResearch = doc.content_research?.section_research;
  if (!sectionResearch || Object.keys(sectionResearch).length === 0) {
    return <div className="text-sm text-gray-500">No research results yet.</div>;
  }
  const sections = doc.article?.sections || [];
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-gray-700 mb-1">Research results by section</div>
      {Object.entries(sectionResearch).map(([sectionId, results]) => {
        const section = sections.find((s) => s.id === sectionId);
        return (
          <div key={sectionId} className="border rounded p-2 text-xs">
            <div className="font-medium text-gray-800 mb-1">
              {section?.heading || sectionId}
            </div>
            {results.map((r, i) => (
              <div key={i} className="ml-2 mb-1 text-gray-600">
                <div className="font-medium">{r.search_query}</div>
                <div className="text-gray-400">{r.type} — {r.purpose}</div>
                {r.results && r.results.length > 0 && (
                  <div className="ml-2 mt-0.5">
                    {r.results.map((result, j) => (
                      <div key={j} className="text-gray-500">
                        {result.title} — <span className="text-blue-500">{result.url}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function Stage4aResearchV2() {
  const { document } = useArticleContext();
  const sections = document?.article?.sections || [];
  const hasResearchNeeds = sections.some(
    (s) => s.research_needs && s.research_needs.length > 0
  );

  return (
    <StagePanel
      stageId="stage_4a_research_enrichment"
      title="Stage 4a: Research Enrichment"
      description="Searches for citable sources and stats for each section's research needs via Tavily."
      runDisabled={!hasResearchNeeds}
      runDisabledReason={!hasResearchNeeds ? 'No sections with research needs defined' : undefined}
      runLabel="Start Research"
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
